import { deleteData, getData, patchData, postData, putData } from '../../DataAccessLayer';
import {
  addAltHostUpdate,
  ats,
  cct,
  enableGuest,
  joinBeforeHostUpdate,
} from '../../DataAccessLayer/services';
import { Time } from '../common/MomentTime';
import {
  MeetingDateFormat,
  MeetingTimeFormat,
} from '../MyCourses/CCT/Body/Zoom/ScheduleMeeting/config';

/**
 * Creates class meetings
 * @param {Object} params The course details, user details
 * @param {Object} params.email The user's email
 * @param {Object} params.course The course details
 * @param {Object} params.midas The user's midas
 * @returns {Promise<Object>} A promise that resolves on creating class meetings
 */
export const createClassMeetings = async ({ email, course, midas }) => {
  const topic =
    course.TERM_CODE + '_' + course.SUBJECT_CODE + '_' + course.COURSE_NUMBER + '_' + course.CRN;
  let agenda = 'Meeting for ' + course.CRNS;
  let meetingDetails = {
    crns: course.CRNS,
    termCode: course.TERM_CODE,
    topic,
    agenda,
    email,
    midas,
    publishKaltura: true,
    joinBeforeHost: false,
    codecs: course.codecs,
  };

  return postData(ats.createMeetings, meetingDetails, true);
};

/**
 * Deletes the course meetings
 * @param {Object} params Course details and the time period of whether all or only future meetings should be deleted
 * @param {Object} params.course The course
 * @param {string} params.period The time period of whether all or only future meetings should be deleted
 * @returns {Promise<Object>} A promise that resolves on deleting the meetings falling within the given time period
 */
export const deleteMeetings = async ({ course, period }) => {
  return deleteData(
    ats.deleteMeetings({
      crn: course.CRN,
      termCode: course.TERM_CODE,
      period,
    }),
    {},
    true,
  );
};

/**
 * Switches the course meetings to personal zoom link
 * @param {Object} params Course details and the personal zoom link
 * @param {Object} params.course The course
 * @param {string} params.link The personal zoom link
 * @returns {Promise<Object>} A promise that resolves on switching the course meetings to personal zoom link
 */
export const switchToPersonalZoom = async ({ course, link }) => {
  return putData(
    ats.personalMeeting({
      crn: course.CRN,
      termCode: course.TERM_CODE,
    }),
    { link },
    true,
  );
};

/**
 * Switches the course meetings to WebEx
 * @param {Object} course The course
 * @returns {Promise<Object>} A promise that resolves on switching the course meetings to WebEx
 */
export const switchToWebEx = async (course) => {
  return putData(
    ats.webex({
      crn: course.CRN,
      termCode: course.TERM_CODE,
    }),
    {},
    true,
  );
};

/**
 * Adds codecs to the course and its meetings
 * If no meeting Ids are passed, then the codecs will be added to the course and all of its meetings.
 * If there are any meeting Ids passed, then the codecs will be added only to those meetings
 * @param {Object} params Course details, codecs, and the meetingIds
 * @param {Object} params.course The course
 * @param {string} params.codecsData The codecs to be added
 * @param {Array} params.meetingIds A list of meetings to which the codecs should be added
 * @returns {Promise<Object>} A promise that resolves on adding codecs
 */
export const addCodecs = ({ crn, termCode, codecsData, meetingIds = [] }) => {
  const payload = {
    codecs: { startDate: codecsData.startDate, endDate: codecsData.endDate },
  };
  payload.codecs.codecsIds = codecsData.codecs.map((codec) => codec.ID);
  payload.meetingIds = meetingIds;

  return postData(
    ats.courseCodecs({
      crn,
      termCode,
    }),
    payload,
    true,
  );
};

/**
 * Adds codecs to multiple course and their meetings
 * @param {Object} params Course details and codecs
 * @param {Object} params.course The course
 * @param {string} params.codecsData The codecs to be added
 * @returns {Promise<Object>} A promise that resolves on adding codecs to multiple courses
 */
export const bulkAddCodecs = async (courses, codecsData) => {
  const payload = {};
  payload.courses = courses.map((course) => ({
    crn: course.CRN,
    startDate: course.START_DATE,
    endDate: course.END_DATE,
    term: course.TERM_CODE,
  }));

  payload.selectedStartDate = codecsData.startDate;
  payload.selectedEndDate = codecsData.endDate;
  payload.codecsIds = codecsData.codecs.map((codec) => codec.ID);

  return postData(ats.bulkCourseCodecs, payload, true);
};

/**
 * Removes codecs from the course and its meetings
 * If no meeting Ids are passed, then the codecs will be removed from the course and all of its meetings.
 * If there are any meeting Ids passed, then the codecs will be removed only from those meetings
 * @param {Object} params Course details, codecs, and the meetingIds
 * @param {Object} params.course The course
 * @param {string} params.codecs The codecs to be removed
 * @param {Array} params.meetingIds A list of meetings to which the codecs should be added
 * @returns {Promise<Object>} A promise that resolves on removing codecs
 */
export const removeCodecs = async ({ crn, termCode, codecs = [], meetingIds = [] }) => {
  const body = {
    uids: codecs,
    meetingIds,
  };

  return deleteData(
    ats.deleteCourseCodecs({
      crn,
      termCode,
    }),
    { data: body },
    true,
  );
};

/**
 * Creates Calendar entries for the course meetings
 * @param {Object} course The course
 * @returns {Promise<Array>} A Promise that resolves with a list of meeting Ids for which the calendar entry creation failed
 */
export const createCalendarEntries = async (course) => {
  // Fetch course meetings
  const courseDetails = await getData(
    cct({
      crn: course.CRN,
      termCode: course.TERM_CODE,
    }),
    true,
  );
  const requests = [];
  const failures = [];
  const success = [];
  courseDetails.meetings.map((meeting) => {
    // Filter the meetings with start time after the current time
    if (
      Time.University(meeting.start_time)
        .add(meeting.duration, 'minute')
        .isSameOrAfter(Time.University())
    ) {
      // Make a request to create calendar entry
      requests.push(
        postData(
          ats.createCalendarEntry({
            termCode: course.TERM_CODE,
            crn: course.CRN,
            meetingId: meeting.id,
          }),
          {},
          true,
        )
          .then((result) => success.push(meeting.id))
          .catch((error) => {
            console.log(error);
            failures.push(meeting.id);
          }),
      );
    }
  });

  await Promise.all(requests);

  return { failures, success };
};

/**
 * Creates adhoc meeting
 * @param {Object} params The course details, adhoc meeting details and the user details
 * @param {Object} params.email The user's email
 * @param {Object} params.course The course details
 * @param {Object} params.adhocMeetingDetails The Adhoc meeting details
 * @param {Object} params.midas The user's midas
 * @returns {Promise<Object>} A promise that resolves on creating adhoc meeting
 */
export const createAdhocMeeting = ({ email, course, midas, adhocMeetingDetails }) => {
  const date = Time.Local(adhocMeetingDetails.date['$d']).format(MeetingDateFormat);
  const beginTime = Time.Local(adhocMeetingDetails.startTime['$d']).format(MeetingTimeFormat);
  //Duration
  const startMoment = Time.Local(adhocMeetingDetails.startTime['$d']);
  const endMoment = Time.Local(adhocMeetingDetails.endTime['$d']);
  const duration = endMoment.diff(startMoment, 'minutes').toString(); //Calculate total duration of meeting from start and end time

  const meetingDetails = {
    crns: course.CRNS,
    termCode: course.TERM_CODE,
    topic: adhocMeetingDetails.meetingTitle,
    agenda: '',
    date,
    beginTime,
    duration,
    record: adhocMeetingDetails.isRecordMeeting,
    waitingRoom: adhocMeetingDetails.isEnableWaitingRoom,
    codecs: adhocMeetingDetails.codecs,
    email,
    midas,
  };

  return postData(ats.adhocMeetings, meetingDetails, true);
};

export const enableGuestSpeaker = ({ crn, termCode, meetingId }) => {
  return patchData(
    enableGuest,
    {
      meetingId,
      crn,
      termCode,
    },
    true,
  );
};

export const addAlternativeHost = ({ crn, termCode, meeting, email }) => {
  const body = {
    email,
    crn,
    termCode,
    meetingId: meeting.id,
    altHosts: meeting.settings.alternative_hosts,
  };
  return patchData(addAltHostUpdate, body, true);
};

export const enableJoinBeforeHost = async ({ crn, termCode, meetingId, joinBeforeHost }) => {
  const body = {
    meetingId,
    crn,
    termCode,
    joinBeforeHost,
  };
  return patchData(joinBeforeHostUpdate, body, true);
};

export const deleteCalendarEntry = async (meetingId) => {
  return deleteData(ats.deleteCalendarEntry(meetingId), {}, true);
};

export const createCalendarEntry = async ({ crn, termCode, meetingId }) => {
  return postData(
    ats.createCalendarEntry({
      crn,
      termCode,
      meetingId,
    }),
    {},
    true,
  );
};

export const deleteMeeting = ({ crn, termCode, meetingId }) => {
  return deleteData(
    ats.deleteMeeting({
      crn,
      termCode,
      meetingId,
    }),
    {},
    true,
  );
};

export const isAddCodecsAllowed = (course) => {
  return Time.University() <= Time.University(course.END_DATE_2);
};
